import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import OptimizedImage from '../components/OptimizedImage';
import SEOHead from '../components/SEOHead';
import { getOptimizedUrl } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_LOGO = process.env.REACT_APP_DEFAULT_LOGO_URL;
const FACEBOOK_PHOTOS = process.env.REACT_APP_FACEBOOK_PHOTOS_URL;

// Helper for logo
const getLogoUrl = (path) => {
  if (!path) return DEFAULT_LOGO;
  return getOptimizedUrl(path, 'medium', 'desktop');
};

const Photos = () => {
  const [albums, setAlbums] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [albumsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/albums`),
        axios.get(`${API}/settings`)
      ]);
      setAlbums(albumsRes.data);
      setSettings(settingsRes.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0a56c2]/20 border-t-[#0a56c2] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Albums laden...</p>
        </div>
      </div>
    );
  }

  // Get first album cover for SEO image
  const seoImage = albums.length > 0 && albums[0].cover_image_url ? albums[0].cover_image_url : null;

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <SEOHead
        title={settings?.gallery_title || 'Foto Galerij'}
        description={settings?.gallery_subtitle || 'Bekijk de mooiste momenten van onze evenementen'}
        image={seoImage}
        url={`${BACKEND_URL}/fotos`}
      />

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#0a56c2] shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <a href="/" className="cursor-pointer">
                <img 
                  src={getLogoUrl(settings?.logo_url)} 
                  alt="Feestcomité Leupegem logo - Terug naar home" 
                  className="h-12 w-auto"
                />
              </a>
            </div>
            <div className="hidden md:flex gap-8">
              <a href="/" className="text-white hover:text-[#ffe980] font-medium transition-colors">Home</a>
              <a href="/#events" className="text-white hover:text-[#ffe980] font-medium transition-colors">Evenementen</a>
              <a href="/fotos" className="text-white/80 border-b-2 border-[#ffe980] font-medium">Foto's</a>
              <a href="/#team" className="text-white hover:text-[#ffe980] font-medium transition-colors">Team</a>
              <a href="/#contact" className="text-white hover:text-[#ffe980] font-medium transition-colors">Contact</a>
            </div>
            <a href="/admin" className="text-sm text-white/80 hover:text-white transition-colors">Admin</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-yellow-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6" data-testid="photos-title">
            {settings?.gallery_title || 'Foto Galerij'}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            {settings?.gallery_subtitle || 'Bekijk de mooiste momenten van onze evenementen'}
          </p>
        </div>
      </section>

      {/* Albums Grid */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {albums.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="albums-grid">
              {albums.map((album, index) => (
                <Link
                  key={album.id}
                  to={`/fotos/${album.id}`}
                  className="group block"
                  data-testid={`album-card-${index}`}
                >
                  <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                    {album.cover_image_url ? (
                      <div className="relative h-56 sm:h-64 overflow-hidden">
                        <OptimizedImage
                          src={album.cover_image_url}
                          alt={`Omslagfoto van fotoalbum: ${album.title}`}
                          variant="cover"
                          sizeType="cover"
                          priority={index < 3}
                          aspectRatio="aspect-[4/3]"
                          className="transform group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <p className="text-sm opacity-90 mb-1">{album.date}</p>
                          <h3 className="text-xl sm:text-2xl font-bold">{album.title}</h3>
                        </div>
                      </div>
                    ) : (
                      <div className="h-56 sm:h-64 bg-gradient-to-br from-blue-100 to-yellow-100 flex items-center justify-center">
                        <div className="text-center p-6">
                          <svg className="w-16 h-16 mx-auto mb-4 text-[#0a56c2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm text-gray-600 mb-1">{album.date}</p>
                          <h3 className="text-xl font-bold text-gray-900">{album.title}</h3>
                        </div>
                      </div>
                    )}
                    <div className="p-5 sm:p-6">
                      <p className="text-gray-600 line-clamp-2">{album.description}</p>
                      <div className="mt-4 flex items-center text-[#0a56c2] font-medium group-hover:gap-3 transition-all">
                        <span>Bekijk album</span>
                        <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <svg className="w-24 h-24 mx-auto mb-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Nog geen foto albums</h3>
              <p className="text-gray-600">Er zijn nog geen foto's toegevoegd. Kom later terug!</p>
            </div>
          )}
        </div>
      </section>

      {/* Facebook Link Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Meer foto's op Facebook</h2>
          <p className="text-base sm:text-lg text-gray-600 mb-8">
            Bekijk al onze foto's en blijf op de hoogte van onze laatste activiteiten
          </p>
          <a
            href={settings?.facebook_photos_url || FACEBOOK_PHOTOS}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#1877f2] hover:bg-[#166fe5] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all hover:scale-105 shadow-lg"
            data-testid="facebook-photos-link"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Bekijk meer op Facebook
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <img 
              src={getLogoUrl(settings?.logo_url)} 
              alt="Feestcomité Leupegem logo" 
              className="h-16 w-auto mx-auto mb-4 brightness-0 invert"
            />
          </div>
          <p className="text-gray-400 mb-4">{settings?.footer_tagline || 'Feestcomité Leupegem - Waar elk feest onvergetelijk wordt'}</p>
          
          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6 mb-6 text-sm text-gray-400">
            <a 
              href={`mailto:${settings?.contact_email || 'leupegemfeestcomite@gmail.com'}`}
              className="hover:text-white transition-colors inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {settings?.contact_email || 'leupegemfeestcomite@gmail.com'}
            </a>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <span className="inline-flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {settings?.contact_address || 'Dries ter Biest 23, 9700 Oudenaarde'}
            </span>
          </div>
          
          <div className="flex justify-center gap-6 mb-6">
            <a 
              href={settings?.facebook_url || "https://www.facebook.com/p/Feestcomit%C3%A9-Leupegem-100064669810907/"} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Volg ons op Facebook"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>
          <p className="text-sm text-gray-500">© {new Date().getFullYear()} Feestcomité Leupegem. Alle rechten voorbehouden.</p>
          <p className="text-xs text-gray-600 mt-4">
            {settings?.footer_credit_text || 'Met veel liefde'} <span className="text-red-500">❤️</span> gemaakt door{' '}
            <a 
              href={settings?.footer_credit_url || "https://www.websitebestellen.be"} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-300 hover:underline transition-colors"
            >
              {settings?.footer_credit_name || 'WebsiteBestellen.be'}
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Photos;
